import { useState, useEffect, useRef } from "react";
import {
  Bell,
  AtSign,
  MessageCircle,
  CheckCircle,
  Eye,
  Edit3,
  TrendingUp,
  UserPlus,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useNotifications, useNotificationCounts, useMarkNotificationRead } from "@/hooks/use-notifications";

const TYPE_CONFIG: Record<string, { icon: typeof Bell; color: string }> = {
  study_alert: { icon: Bell, color: "text-red-500" },
  comment_mention: { icon: AtSign, color: "text-blue-500" },
  comment_reply: { icon: MessageCircle, color: "text-blue-500" },
  review_decision: { icon: CheckCircle, color: "text-green-500" },
  review_requested: { icon: Eye, color: "text-amber-500" },
  artifact_change: { icon: Edit3, color: "text-purple-500" },
  enrollment_milestone: { icon: TrendingUp, color: "text-emerald-500" },
  team_member_joined: { icon: UserPlus, color: "text-indigo-500" },
  edc_sync_failure: { icon: AlertTriangle, color: "text-red-500" },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();

  const { data: counts } = useNotificationCounts();
  const { data: notifData } = useNotifications({ limit: 10 });
  const notifications = notifData?.notifications ?? [];
  const unreadCount = counts?.totalUnread ?? 0;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  function handleNotificationClick(notif: (typeof notifications)[0]) {
    setOpen(false);

    // Mark as read
    if (!notif.read) {
      markRead.mutate({ notificationId: notif.id, orgId: notif.orgId });
    }

    // Deep link
    const meta = notif.metadata;
    if (meta.studyId) {
      navigate(`/studies/${meta.studyId}`);
    } else if (meta.alertId) {
      navigate("/alerts");
    } else {
      navigate("/notifications");
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full z-50 mt-1 w-[calc(100vw-2rem)] max-w-sm rounded-lg border border-border bg-card shadow-lg sm:w-80"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Notifications
            </span>
            {unreadCount > 0 && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                {unreadCount} new
              </span>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {notifications.map((notif) => {
                const config = TYPE_CONFIG[notif.type] ?? { icon: Bell, color: "text-muted-foreground" };
                const Icon = config.icon;

                return (
                  <button
                    key={`${notif.orgId}-${notif.id}`}
                    onClick={() => handleNotificationClick(notif)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                      notif.read ? "opacity-50" : ""
                    }`}
                  >
                    <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${config.color}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-sm font-medium text-foreground">{notif.title}</p>
                        {!notif.read && <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">{notif.message}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground">{timeAgo(notif.createdAt)}</span>
                        <span className="rounded bg-muted px-1 py-0.5 text-[9px] text-muted-foreground">
                          {notif.orgName}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="border-t border-border px-4 py-2">
            <button
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
              className="w-full text-center text-xs font-medium text-primary hover:text-primary/80"
              data-testid="view-all-notifications"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
